/**
 * As candidatas de imagem do caso, lidas ao montar e relidas enquanto alguma leitura proposta
 * estiver em curso (E114.F3.C02, F-E114.F3-04).
 *
 * A leitura proposta da imagem de documento roda em segundo plano depois do upload do pacote e
 * do "não é retrato". Sem a releitura, quem abria a tela antes de ela terminar recebia o cartão
 * sem a proposta e ficava assim até sair e voltar. O servidor diz quais imagens ainda estão
 * sendo lidas (`leituras_em_curso`, com prazo contra tarefa perdida), e a próxima consulta só é
 * agendada depois que a anterior volta — nunca empilha pedidos (E113.F1.T03).
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { listarCandidatasDeImagem, type CandidatasDoCasoData } from '@/lib/candidatas_de_imagem';

export const INTERVALO_DA_LEITURA_EM_CURSO_MS = 2500;

export interface CandidatasDoCaso {
  dados: CandidatasDoCasoData | null;
  /** O erro da seção: o da primeira leitura (`falhaAoCarregar`) ou o de uma ação do perito. */
  erro: string | null;
  setErro: (erro: string | null) => void;
  /** Quantas leituras em segundo plano (leitura proposta por imagem, classificação) ainda correm. */
  emCurso: number;
  carregar: () => Promise<void>;
}

export function useCandidatasDoCaso(
  client: AxiosInstance,
  caseId: string,
  falhaAoCarregar: string,
): CandidatasDoCaso {
  const [dados, setDados] = useState<CandidatasDoCasoData | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Uma consulta de releitura que falhou reagenda a seguinte, em vez de parar a espera.
  const [tentativa, setTentativa] = useState(0);

  const carregar = useCallback(async (): Promise<void> => {
    setDados(await listarCandidatasDeImagem(client, caseId));
  }, [client, caseId]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lidas = await listarCandidatasDeImagem(client, caseId);
        if (!cancelado) setDados(lidas);
      } catch {
        if (!cancelado) setErro(falhaAoCarregar);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId, falhaAoCarregar]);

  // E122.F2: a classificação das candidatas também roda em segundo plano e também é esperada.
  const emCurso =
    (dados?.leituras_em_curso?.length ?? 0) + (dados?.classificacao_em_curso === true ? 1 : 0);

  useEffect(() => {
    if (emCurso === 0) return undefined;
    let cancelado = false;
    const agendada = window.setTimeout(() => {
      void listarCandidatasDeImagem(client, caseId)
        .then((lidas) => {
          if (!cancelado) setDados(lidas);
        })
        .catch(() => {
          if (!cancelado) setTentativa((n) => n + 1);
        });
    }, INTERVALO_DA_LEITURA_EM_CURSO_MS);
    return () => {
      cancelado = true;
      window.clearTimeout(agendada);
    };
  }, [client, caseId, dados, emCurso, tentativa]);

  return { dados, erro, setErro, emCurso, carregar };
}
